import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Icon,
  Spinner,
  IconButton,
  Badge,
  Flex,
  Input,
  NativeSelect,
  useDisclosure,
  ButtonGroup,
  SimpleGrid,
  Alert,
  Dialog,
  Portal,
  Field,
} from '@chakra-ui/react';
import { toaster, toast } from '@/components/ui/toaster';
import { Tooltip } from '@/components/ui/tooltip';
import { FaPlus, FaEdit, FaTrash, FaLayerGroup, FaThLarge, FaDotCircle, FaFilePdf } from 'react-icons/fa';
import AppLayout from './AppLayout.jsx';
import FormacionEditor from './FormacionEditor.jsx';
import { formacionesAPI, musicosAPI } from '../services/api';
import { useProductTour } from '../hooks/useProductTour';
import { bandaFormacionesSteps } from '../tours/bandaFormacionesTour';

function requiereMusicosParaFormaciones(musicos) {
  if ((musicos || []).length > 0) return true;
  toaster.create({
    title: 'Añade músicos en Mis músicos',
    description: 'Necesitas al menos un músico en el censo de la banda para crear nuevas formaciones.',
    type: 'warning',
    duration: 5000,
    closable: true,
  });
  return false;
}

// ── helpers PDF ───────────────────────────────────────────────────────────────
function escHtml(s) {
  if (!s) return '';
  return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

async function fetchImgDataUrl(origin, path) {
  try {
    const res = await fetch(`${origin}${path}`);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise(resolve => {
      const fr = new FileReader();
      fr.onloadend = () => resolve(String(fr.result || ''));
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch { return null; }
}

const TIPO_FULL = { PROCESION: 'Procesión', CONCIERTO: 'Concierto', CERTAMEN: 'Certamen', OTRO: 'Otro' };

function buildFormacionGridHtml(fd) {
  const { filas, columnas, tieneDirector, tieneFilaExtra,
          filaExtraCeldas = 6, filaExtraPos = 'before', filaExtraEtiqueta } = fd;

  const posMap = {};
  for (const p of fd.posiciones) posMap[`${p.fila}-${p.columna}`] = p;

  const cell = (pos, compact = false) => {
    if (!pos?.musico) {
      const inst = pos?.instrumento?.nombre || pos?.instrumentoPersonalizado?.nombre || '';
      return `<div style="background:#F3F0FF;border:1.5px dashed #C4B5FD;border-radius:8px;`
        + `min-height:${compact ? 60 : 72}px;display:flex;align-items:center;justify-content:center;`
        + `font-size:9px;color:#9F7AEA;opacity:0.7;text-align:center;padding:4px">${escHtml(inst)}</div>`;
    }
    const m = pos.musico;
    const ia = m.instrumentosAsignados?.[0];
    const iaNom = ia?.instrumento?.nombre || ia?.instrumentoPersonalizado?.nombre || '';
    const voz = ia?.vozPartituraPersonalizada?.nombre || '';
    const ap = m.apellidos ? ` ${m.apellidos.trim().split(/\s+/)[0][0]}.` : '';
    const mh = compact ? 60 : 72;
    return `<div style="background:#FAF5FF;border:2px solid #B794F4;border-radius:8px;`
      + `min-height:${mh}px;display:flex;flex-direction:column;align-items:center;`
      + `justify-content:center;text-align:center;gap:2px;padding:4px 3px">
        <div style="font-size:${compact ? 8 : 9}px;font-weight:800;color:#2D1B69;line-height:1.2">
          ${escHtml(m.nombre)}${escHtml(ap)}</div>
        ${iaNom ? `<div style="font-size:8px;color:#6B46C1;opacity:0.9">${escHtml(iaNom)}${voz ? ` <em>${escHtml(voz)}</em>` : ''}</div>` : ''}
      </div>`;
  };

  const emptyBox = (h = 72) =>
    `<div style="background:#F7FAFC;border:1.5px dashed #E2E8F0;border-radius:8px;min-height:${h}px;opacity:0.3"></div>`;

  const gridRow = (filaIdx, nCols, etiqueta) => {
    const cols = Array.from({ length: nCols }, (_, c) => {
      const p = posMap[`${filaIdx}-${c}`];
      return `<div>${p ? cell(p, nCols > 8) : emptyBox(nCols > 8 ? 60 : 72)}</div>`;
    }).join('');
    const label = etiqueta ? `<div style="font-size:9px;color:#718096;font-style:italic;margin-bottom:4px;text-align:center">${escHtml(etiqueta)}</div>` : '';
    return `${label}<div style="display:grid;grid-template-columns:repeat(${nCols},1fr);gap:5px;margin-bottom:8px">${cols}</div>`;
  };

  const dirRow = () => {
    const p = posMap['-1-0'];
    return `<div style="display:flex;justify-content:center;margin-bottom:8px">
      <div style="width:110px">${p ? cell(p) : emptyBox()}</div>
    </div>`;
  };

  const extraRow = () => gridRow(-2, filaExtraCeldas, filaExtraEtiqueta || 'Fila extraordinaria');

  let html = '';
  if (tieneFilaExtra && filaExtraPos === 'before') html += extraRow();
  if (tieneDirector) html += dirRow();
  html += Array.from({ length: filas }, (_, r) => gridRow(r, columnas, '')).join('');
  if (tieneFilaExtra && filaExtraPos === 'after') html += extraRow();
  return html;
}

function buildFormacionLibreHtml(fd) {
  const placed = fd.posiciones.filter(p => p.musico && p.x != null && p.y != null);
  const subtipo = fd.subtipoLibre || 'teatro';
  const W = 720, H = 420;

  let guide = '';
  if (subtipo === 'calle') {
    guide = `<svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}"
        style="position:absolute;inset:0;pointer-events:none;color:#6B46C1">
      <rect x="${W*0.02}" y="0" width="${W*0.12}" height="${H}" fill="currentColor" opacity="0.06" rx="4"/>
      <line x1="${W*0.14}" y1="0" x2="${W*0.14}" y2="${H}" stroke="currentColor" stroke-width="2" opacity="0.18" stroke-dasharray="8 5"/>
      <rect x="${W*0.86}" y="0" width="${W*0.12}" height="${H}" fill="currentColor" opacity="0.06" rx="4"/>
      <line x1="${W*0.86}" y1="0" x2="${W*0.86}" y2="${H}" stroke="currentColor" stroke-width="2" opacity="0.18" stroke-dasharray="8 5"/>
      <line x1="${W*0.5}" y1="${H*0.05}" x2="${W*0.5}" y2="${H*0.95}" stroke="currentColor" stroke-width="1" opacity="0.1" stroke-dasharray="12 8"/>
      <g opacity="0.22">
        <line x1="${W*0.5}" y1="${H*0.85}" x2="${W*0.5}" y2="${H*0.12}" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
        <polyline points="${W*0.44},${H*0.22} ${W*0.5},${H*0.1} ${W*0.56},${H*0.22}"
          fill="none" stroke="currentColor" stroke-width="3" stroke-linejoin="round" stroke-linecap="round"/>
      </g>
    </svg>`;
  } else {
    guide = `<svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}"
        style="position:absolute;inset:0;pointer-events:none;color:#6B46C1;opacity:0.13">
      ${[85,70,55,40].map(r =>
        `<ellipse cx="${W*0.5}" cy="${H*0.95}" rx="${W*r/100}" ry="${H*r*0.55/100}"
          fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="6 4"/>`
      ).join('')}
      <line x1="${W*0.05}" y1="${H*0.9}" x2="${W*0.95}" y2="${H*0.9}" stroke="currentColor" stroke-width="2"/>
    </svg>`;
  }

  const chips = placed.map(p => {
    const m = p.musico;
    const ia = m.instrumentosAsignados?.[0];
    const iaNom = ia?.instrumento?.nombre || ia?.instrumentoPersonalizado?.nombre || '';
    const ap = m.apellidos ? ` ${m.apellidos.trim().split(/\s+/)[0][0]}.` : '';
    return `<div style="position:absolute;left:${p.x}%;top:${p.y}%;transform:translate(-50%,-50%)">
      <div style="background:#FAF5FF;border:2px solid #B794F4;border-radius:8px;padding:5px 6px;
        min-width:75px;max-width:105px;text-align:center;box-shadow:0 2px 6px rgba(107,70,193,0.15)">
        <div style="font-size:9px;font-weight:800;color:#2D1B69;line-height:1.2">${escHtml(m.nombre)}${escHtml(ap)}</div>
        ${iaNom ? `<div style="font-size:8px;color:#6B46C1;opacity:0.9">${escHtml(iaNom)}</div>` : ''}
      </div>
    </div>`;
  }).join('');

  return `<div style="position:relative;width:${W}px;height:${H}px;background:#F8F5FF;border-radius:12px;overflow:hidden;margin:0 auto">
    ${guide}${chips}
  </div>`;
}

const TIPO_LABELS = {
  PROCESION: 'Procesión',
  CONCIERTO: 'Concierto',
  CERTAMEN: 'Certamen',
  OTRO: 'Otro',
};

const TIPO_COLORS = {
  PROCESION: 'purple',
  CONCIERTO: 'blue',
  CERTAMEN: 'orange',
  OTRO: 'gray',
};

export default function Formaciones() {
  const navigate = useNavigate();
  useProductTour('banda-formaciones', bandaFormacionesSteps, { autoStart: false });
  const [formaciones, setFormaciones] = useState([]);
  const [musicos, setMusicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const { open: isOpen, onOpen, onClose } = useDisclosure();
  const {
    open: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const cancelDeleteRef = useRef(null);
  const [formacionPendienteEliminar, setFormacionPendienteEliminar] = useState(null);
  const [deletingFormacion, setDeletingFormacion] = useState(false);
  const [form, setForm] = useState({ nombre: '', tipo: 'PROCESION', modo: 'GRID' });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pdfGenerando, setPdfGenerando] = useState(false);
  

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const activeBg = useColorModeValue('purple.50', 'purple.900');
  const emptyColor = useColorModeValue('gray.300', 'gray.600');
  const subtleBg = useColorModeValue('gray.50', 'gray.900');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [pRes, mRes] = await Promise.all([
        formacionesAPI.getPlantillas(),
        musicosAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setMusicos(Array.isArray(mRes.data) ? mRes.data : []);
      const data = pRes.data || [];
      setFormaciones(data);
      setSelected(prev => {
        if (!prev && data.length > 0) return data[0];
        if (prev) return data.find(f => f.id === prev.id) || data[0] || null;
        return null;
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    if (!requiereMusicosParaFormaciones(musicos)) return;
    setEditingId(null);
    setForm({ nombre: '', tipo: 'PROCESION', modo: 'GRID' });
    onOpen();
  };

  const openEdit = (f) => {
    setEditingId(f.id);
    setForm({ nombre: f.nombre, tipo: f.tipo, modo: f.modo || 'GRID' });
    onOpen();
  };

  const handleSave = async () => {
    if (!editingId && !requiereMusicosParaFormaciones(musicos)) return;
    if (!form.nombre.trim()) {
      toaster.create({ title: 'El nombre es obligatorio', type: 'warning' });
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        await formacionesAPI.updatePlantilla(editingId, form);
        toaster.create({ title: 'Formación actualizada', type: 'success' });
      } else {
        const res = await formacionesAPI.createPlantilla({ ...form, filas: 8, columnas: 6 });
        toaster.create({ title: 'Formación creada', type: 'success' });
        // auto-select the new one after fetch
        const newId = res.data?.id;
        await fetchData();
        if (newId) setSelected(prev => prev); // fetchData sets it
      }
      onClose();
      if (editingId) fetchData();
    } catch {
      toaster.create({ title: 'Error al guardar', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleExportFormacionPDF = async (plantillaBase) => {
    const w = window.open('', '_blank');
    if (!w) {
      toaster.create({ title: 'Permite ventanas emergentes', type: 'warning' });
      return;
    }
    setPdfGenerando(true);

    // Cargar datos completos con posiciones y músicos
    let fd;
    try {
      const res = await formacionesAPI.getPlantilla(plantillaBase.id);
      fd = res.data;
    } catch {
      toaster.create({ title: 'Error al cargar la formación', type: 'error' });
      w.close();
      setPdfGenerando(false);
      return;
    }

    const origin = window.location.origin;
    const [logoDataUrl, musicoDataUrl] = await Promise.all([
      fetchImgDataUrl(origin, '/logo%20y%20texto%20cadencya%20blanco.png'),
      fetchImgDataUrl(origin, '/musico-cofradia-1.png'),
    ]);

    const IMG_LOGO = logoDataUrl
      ? `<img src="${logoDataUrl}" alt="Cadencya" style="height:42px;display:block">`
      : `<span style="font-size:21px;font-weight:900;color:#fff">Cadencya</span>`;
    const IMG_LOGO_SM = logoDataUrl
      ? `<img src="${logoDataUrl}" alt="Cadencya" style="height:28px;display:block">`
      : `<span style="font-size:14px;font-weight:900;color:#fff">Cadencya</span>`;
    const IMG_MUSICO_HD = musicoDataUrl
      ? `<img src="${musicoDataUrl}" alt="" style="height:70px;opacity:0.32;display:block;object-fit:contain">` : '';
    const IMG_MUSICO_SM = musicoDataUrl
      ? `<img src="${musicoDataUrl}" alt="" style="height:32px;opacity:0.4;display:block;object-fit:contain">` : '';

    const isGrid = (fd.modo || 'GRID') === 'GRID';
    const modoLabel = isGrid
      ? `${fd.filas} filas × ${fd.columnas} columnas`
      : (fd.subtipoLibre === 'calle' ? 'Modo libre — Calle' : 'Modo libre — Teatro/Auditorio');
    const totalMusicos = fd.posiciones.filter(p => p.musico).length;
    const now = new Date().toLocaleString('es-ES', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    const layoutHtml = isGrid ? buildFormacionGridHtml(fd) : buildFormacionLibreHtml(fd);

    // Lista de músicos en la formación (sidebar)
    const musicosAsignados = fd.posiciones
      .filter(p => p.musico)
      .sort((a, b) => {
        const iaNomA = a.musico.instrumentosAsignados?.[0]?.instrumento?.nombre || a.musico.instrumentosAsignados?.[0]?.instrumentoPersonalizado?.nombre || '';
        const iaNomB = b.musico.instrumentosAsignados?.[0]?.instrumento?.nombre || b.musico.instrumentosAsignados?.[0]?.instrumentoPersonalizado?.nombre || '';
        return iaNomA.localeCompare(iaNomB, 'es');
      });

    const musicoListHtml = musicosAsignados.length > 0 ? `
      <div style="margin-top:28px">
        <div class="section-title">Músicos en la formación (${musicosAsignados.length})</div>
        <table>
          <thead><tr><th>Nombre</th><th>Instrumento</th></tr></thead>
          <tbody>${musicosAsignados.map(p => {
            const m = p.musico;
            const ia = m.instrumentosAsignados?.[0];
            const iaNom = ia?.instrumento?.nombre || ia?.instrumentoPersonalizado?.nombre || '—';
            return `<tr><td><strong>${escHtml(m.nombre)} ${escHtml(m.apellidos || '')}</strong></td><td>${escHtml(iaNom)}</td></tr>`;
          }).join('')}</tbody>
        </table>
      </div>` : '';

    w.document.write(`<!DOCTYPE html>
<html lang="es"><head>
<meta charset="utf-8">
<title>Formación · ${escHtml(fd.nombre)}</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Segoe UI',system-ui,sans-serif;color:#1A1A2E;background:#fff;font-size:13px;line-height:1.55}
.hd{background:linear-gradient(135deg,#2D1B69 0%,#6B46C1 55%,#9F7AEA 100%);display:flex;align-items:center;justify-content:space-between;padding:22px 32px;gap:16px;position:relative;overflow:hidden}
.hd::after{content:'';position:absolute;right:90px;bottom:-30px;width:120px;height:120px;background:rgba(255,255,255,0.04);border-radius:50%}
.hd-logo{display:flex;align-items:center;flex-shrink:0}
.hd-center{flex:1;text-align:center;padding:0 16px}
.hd-label{font-size:9px;color:#C4B5FD;letter-spacing:2px;text-transform:uppercase;margin-bottom:5px}
.hd-name{font-size:19px;font-weight:800;color:#fff;line-height:1.25}
.hd-sub{font-size:12px;color:#E9D8FD;margin-top:3px}
.hd-deco{flex-shrink:0;opacity:0.28}
.hd-stripe{height:4px;background:linear-gradient(90deg,#553C9A,#B794F4,#6B46C1)}
.meta{display:flex;align-items:center;gap:10px;padding:14px 32px 0;flex-wrap:wrap}
.badge{display:inline-block;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:700}
.badge-purple{background:#EDE9FE;color:#553C9A}
.badge-gray{background:#EDF2F7;color:#4A5568}
.section-title{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.2px;color:#6B46C1;border-bottom:1.5px solid #E9D8FD;padding-bottom:5px;margin:20px 0 12px}
.content{padding:20px 32px 28px}
table{width:100%;border-collapse:collapse;margin-top:8px;font-size:12px}
thead tr{background:linear-gradient(135deg,#553C9A,#7C3AED);color:#fff}
thead th{padding:7px 10px;text-align:left;font-weight:600;font-size:11px}
tbody tr:nth-child(even){background:#FAF5FF}
tbody td{padding:6px 10px;border-bottom:1px solid #EDE9FE;color:#2D1B69}
.footer{background:linear-gradient(135deg,#1A0D40 0%,#2D1B69 100%);padding:11px 32px;display:flex;align-items:center;justify-content:space-between;margin-top:32px}
.footer-brand{display:flex;flex-direction:column;gap:3px}
.footer-tagline{font-size:10px;color:#B794F4}
.footer-meta{font-size:10px;color:#9F7AEA;text-align:right}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style>
</head><body>

<div class="hd">
  <div class="hd-logo">${IMG_LOGO}</div>
  <div class="hd-center">
    <div class="hd-label">Ficha de Formación</div>
    <div class="hd-name">${escHtml(fd.nombre)}</div>
    <div class="hd-sub">${escHtml(TIPO_FULL[fd.tipo] || fd.tipo)} · ${escHtml(modoLabel)}</div>
  </div>
  <div class="hd-deco">${IMG_MUSICO_HD}</div>
</div>
<div class="hd-stripe"></div>

<div class="meta">
  <span class="badge badge-purple">${escHtml(TIPO_FULL[fd.tipo] || fd.tipo)}</span>
  <span class="badge badge-gray">${escHtml(modoLabel)}</span>
  <span class="badge badge-gray">${totalMusicos} músicos asignados</span>
  ${fd.tieneDirector ? '<span class="badge badge-purple">Con director</span>' : ''}
  ${fd.tieneFilaExtra ? `<span class="badge badge-gray">${escHtml(fd.filaExtraEtiqueta || 'Fila extraordinaria')}</span>` : ''}
</div>

<div class="content">
  <div class="section-title">Distribución</div>
  ${layoutHtml}
  ${musicoListHtml}
</div>

<div class="footer">
  <div class="footer-brand">
    ${IMG_LOGO_SM}
    <div class="footer-tagline">La herramienta de tu banda</div>
  </div>
  <div class="footer-meta">Generado el ${now}<br>cadencya.app</div>
  <div>${IMG_MUSICO_SM}</div>
</div>

</body></html>`);

    w.document.close();
    setTimeout(() => {
      try { w.focus(); w.print(); } catch {}
      setPdfGenerando(false);
    }, 300);
  };

  const requestDeleteFormacion = (f) => {
    setFormacionPendienteEliminar(f);
    onDeleteOpen();
  };

  const confirmDeleteFormacion = async () => {
    if (!formacionPendienteEliminar?.id) return;
    try {
      setDeletingFormacion(true);
      await formacionesAPI.deletePlantilla(formacionPendienteEliminar.id);
      toaster.create({ title: 'Formación eliminada', type: 'info' });
      if (selected?.id === formacionPendienteEliminar.id) setSelected(null);
      await fetchData();
    } catch {
      toaster.create({ title: 'Error al eliminar', type: 'error' });
    } finally {
      setDeletingFormacion(false);
      setFormacionPendienteEliminar(null);
      onDeleteClose();
    }
  };

  const formacionesBloqueadas = !loading && musicos.length === 0;

  return (
    <AppLayout userType="banda">
      {formacionesBloqueadas && (
        <Box px={{ base: 4, md: 6 }} py={3} bg={subtleBg} borderBottom="1px solid" borderColor={borderColor} data-tour="formaciones-bloqueada-alert">
          <Alert.Root
            status="warning"
            variant='subtle'
            borderRadius="lg"
            borderStartWidth='3px'
            borderStartColor='colorPalette.solid'>
            <Alert.Indicator />
            <Box>
              <Alert.Title fontSize="sm">Esta sección está bloqueada hasta tener músicos</Alert.Title>
              <Alert.Description fontSize="sm" display="block">
                <Text mb={3}>
                  En <strong>Mis músicos</strong> aún no hay ningún integrante registrado. Las formaciones se montan
                  colocando músicos en la plantilla; añade antes al menos un músico al censo de la banda y podrás crear
                  formaciones y usar el editor.
                </Text>
                <Button size="sm" colorPalette="orange" onClick={() => navigate('/banda/musicos')}>
                  Ir a Mis músicos
                </Button>
              </Alert.Description>
            </Box>
          </Alert.Root>
        </Box>
      )}

      <Flex
        h="calc(100vh - 112px)"
        overflow="hidden"
        opacity={formacionesBloqueadas ? 0.5 : 1}
        pointerEvents={formacionesBloqueadas ? 'none' : 'auto'}
        transition="opacity 0.2s ease"
      >

        {/* ── LEFT: lista de formaciones ── */}
        <Box
          w={{ base: '100%', md: '240px' }}
          flexShrink={0}
          borderRight="1px solid"
          borderColor={borderColor}
          bg={bg}
          display="flex"
          flexDirection="column"
          overflow="hidden"
          data-tour="formaciones-sidebar"
        >
          {/* Cabecera */}
          <Box px={3} pt={4} pb={3} borderBottom="1px solid" borderColor={borderColor}>
            <Text
              fontSize="2xs"
              fontWeight="700"
              color="gray.400"
              textTransform="uppercase"
              letterSpacing="widest"
              mb={formacionesBloqueadas ? 1 : 3}
            >
              Mis formaciones
            </Text>
            {formacionesBloqueadas && (
              <Text fontSize="2xs" color="gray.500" mb={3}>
                Añade músicos en Mis músicos para habilitar esta sección
              </Text>
            )}
            <Tooltip
              content={formacionesBloqueadas ? 'Añade músicos en Mis músicos primero' : 'Crear plantilla de formación'}
              showArrow
            >
              <Button
                colorPalette="purple"
                size="sm"
                w="full"
                rounded="lg"
                disabled={formacionesBloqueadas}
                onClick={openCreate}
                data-tour={formacionesBloqueadas ? undefined : 'formaciones-nueva'}><FaPlus />Nueva formación
                              </Button>
            </Tooltip>
          </Box>

          {/* Lista */}
          <Box flex={1} overflowY="auto" p={2}>
            {loading ? (
              <Flex justify="center" py={8}>
                <Spinner color="purple.500" />
              </Flex>
            ) : formaciones.length === 0 ? (
              <VStack py={10} gap={3} color={emptyColor}>
                <Icon boxSize={10} asChild><FaLayerGroup /></Icon>
                <Text fontSize="sm" textAlign="center">
                  Aún no hay formaciones.{'\n'}Crea la primera.
                </Text>
              </VStack>
            ) : (
              <VStack gap={1} align="stretch">
                {formaciones.map(f => {
                  const active = selected?.id === f.id;
                  return (
                    <Box
                      key={f.id}
                      p={3}
                      rounded="xl"
                      bg={active ? activeBg : 'transparent'}
                      border="1.5px solid"
                      borderColor={active ? 'purple.300' : 'transparent'}
                      cursor="pointer"
                      _hover={{ bg: active ? activeBg : hoverBg }}
                      transition="all 0.15s"
                      onClick={() => setSelected(f)}
                    >
                      <HStack justify="space-between" align="start">
                        <VStack gap={1} align="start" flex={1} minW={0}>
                          <Text
                            fontWeight="700"
                            fontSize="sm"
                            color={active ? 'purple.700' : 'inherit'}
                            lineClamp={1}
                          >
                            {f.nombre}
                          </Text>
                          <HStack gap={1}>
                            <Badge colorPalette={TIPO_COLORS[f.tipo]} fontSize="2xs">
                              {TIPO_LABELS[f.tipo]}
                            </Badge>
                            <Badge colorPalette={f.modo === 'LIBRE' ? 'blue' : 'gray'} fontSize="2xs" variant="outline">
                              {f.modo === 'LIBRE' ? 'Libre' : 'Grid'}
                            </Badge>
                          </HStack>
                        </VStack>
                        <HStack gap={0} flexShrink={0}>
                          <Tooltip content="Renombrar">
                            <IconButton
                              size="xs"
                              variant="ghost"
                              colorPalette={active ? 'purple' : 'gray'}
                              onClick={e => { e.stopPropagation(); openEdit(f); }}
                              aria-label="Editar"><FaEdit /></IconButton>
                          </Tooltip>
                          <Tooltip content="Eliminar">
                            <IconButton
                              size="xs"
                              variant="ghost"
                              colorPalette="red"
                              onClick={e => { e.stopPropagation(); requestDeleteFormacion(f); }}
                              aria-label="Eliminar"><FaTrash /></IconButton>
                          </Tooltip>
                        </HStack>
                      </HStack>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </Box>
        </Box>

        {/* ── RIGHT: editor ── */}
        <Box flex={1} minW={0} overflowY="auto" bg={useColorModeValue('gray.50', 'gray.900')} data-tour={formacionesBloqueadas ? undefined : 'formaciones-editor'}>
          {selected ? (
            <Box display="flex" flexDirection="column" h="full">
              {/* Toolbar con botón PDF */}
              <Flex
                px={4} py={2}
                align="center"
                justify="flex-end"
                borderBottom="1px solid"
                borderColor={borderColor}
                bg={bg}
                flexShrink={0}
                gap={2}
              >
                <Button
                  size="sm"
                  variant="outline"
                  colorPalette="purple"
                  rounded="full"
                  loading={pdfGenerando}
                  loadingText="Generando…"
                  onClick={() => handleExportFormacionPDF(selected)}><FaFilePdf />Exportar PDF
                                  </Button>
              </Flex>
              <Box flex={1} overflow="auto">
                <FormacionEditor
                  key={selected.id}
                  plantilla={selected}
                  toast={toast}
                  embedded
                  onSaved={fetchData}
                />
              </Box>
            </Box>
          ) : (
            <Flex h="full" align="center" justify="center" direction="column" gap={4} color={emptyColor}>
              <Icon boxSize={20} asChild><FaLayerGroup /></Icon>
              <VStack gap={1}>
                <Text fontWeight="700" fontSize="lg" color="gray.500">
                  Selecciona una formación
                </Text>
                <Text fontSize="sm" color="gray.400">
                  O crea una nueva desde el panel lateral
                </Text>
              </VStack>
            </Flex>
          )}

        </Box>
      </Flex>

      {/* Modal crear / renombrar */}
      <Dialog.Root open={isOpen} size='sm' onOpenChange={e => {
        if (!e.open) {
          onClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>{editingId ? 'Renombrar formación' : 'Nueva formación'}</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <VStack gap={4}>
                  <Field.Root required>
                    <Field.Label fontSize="sm">Nombre</Field.Label>
                    <Input
                      size="sm"
                      value={form.nombre}
                      onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                      placeholder="Ej: Formación calle, Formación concierto…"
                      onKeyDown={e => e.key === 'Enter' && handleSave()}
                      autoFocus
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">Tipo</Field.Label>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        size="sm"
                        value={form.tipo}
                        onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
                        {Object.entries(TIPO_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                  {!editingId && (
                    <Field.Root>
                      <Field.Label fontSize="sm">Modo de distribución</Field.Label>
                      <SimpleGrid columns={2} gap={2}>
                        <Box
                          border="2px solid"
                          borderColor={form.modo === 'GRID' ? 'purple.500' : 'gray.200'}
                          bg={form.modo === 'GRID' ? 'purple.50' : 'white'}
                          rounded="xl"
                          p={3}
                          textAlign="center"
                          transition="all 0.15s"
                          _hover={{ borderColor: 'purple.300' }}
                          asChild><button type="button" onClick={() => setForm(f => ({ ...f, modo: 'GRID' }))}>
                            <Icon
                              color={form.modo === 'GRID' ? 'purple.500' : 'gray.400'}
                              boxSize={6}
                              mb={1}
                              asChild><FaThLarge /></Icon>
                            <Text fontSize="xs" fontWeight="700" color={form.modo === 'GRID' ? 'purple.700' : 'gray.600'}>
                              Cuadrícula
                            </Text>
                            <Text fontSize="2xs" color="gray.400">Desfiles, procesiones</Text>
                          </button></Box>
                        <Box
                          border="2px solid"
                          borderColor={form.modo === 'LIBRE' ? 'blue.500' : 'gray.200'}
                          bg={form.modo === 'LIBRE' ? 'blue.50' : 'white'}
                          rounded="xl"
                          p={3}
                          textAlign="center"
                          transition="all 0.15s"
                          _hover={{ borderColor: 'blue.300' }}
                          asChild><button type="button" onClick={() => setForm(f => ({ ...f, modo: 'LIBRE' }))}>
                            <Icon
                              color={form.modo === 'LIBRE' ? 'blue.500' : 'gray.400'}
                              boxSize={6}
                              mb={1}
                              asChild><FaDotCircle /></Icon>
                            <Text fontSize="xs" fontWeight="700" color={form.modo === 'LIBRE' ? 'blue.700' : 'gray.600'}>
                              Lienzo libre
                            </Text>
                            <Text fontSize="2xs" color="gray.400">Teatros, conciertos</Text>
                          </button></Box>
                      </SimpleGrid>
                    </Field.Root>
                  )}
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onClose}>Cancelar</Button>
                <Button colorPalette="purple" onClick={handleSave} loading={saving}>
                  {editingId ? 'Guardar' : 'Crear'}
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root
        open={isDeleteOpen}
        initialFocusEl={() => cancelDeleteRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            if (deletingFormacion) return;
            setFormacionPendienteEliminar(null);
            onDeleteClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header fontSize="lg" fontWeight="bold">
                  Eliminar formación
                </Dialog.Header>
                <Dialog.Body>
                  <Text fontSize="sm" color="gray.700">
                    ¿Eliminar la formación{' '}
                    <strong>{formacionPendienteEliminar?.nombre || 'seleccionada'}</strong> y todas sus posiciones? Esta acción no se puede deshacer.
                  </Text>
                </Dialog.Body>
                <Dialog.Footer gap={3}>
                  <Button
                    ref={cancelDeleteRef}
                    onClick={() => {
                      setFormacionPendienteEliminar(null);
                      onDeleteClose();
                    }}
                    disabled={deletingFormacion}
                  >
                    Cancelar
                  </Button>
                  <Button colorPalette="red" onClick={confirmDeleteFormacion} loading={deletingFormacion}>
                    Eliminar
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>
    </AppLayout>
  );
}
